// React Imports
import { useState, useEffect, useRef } from "react";

// Toolkit Imports
import { initPathCompletedChannel } from "sj-ui-toolkit/src/client/actionCable";
import { fetchUserPathData } from "sj-ui-toolkit/src/api";

// Feature Flags
import { useFlags } from "launchdarkly-react-client-sdk";

/**
 * Hook that subscribes to the PathCompletedChannel via Action Cable.
 *
 * When the server broadcasts a path-completion event (during a lesson),
 * it silently stores the data. The consuming component can check
 * `isPathCompleted` after a lesson finishes to decide whether to show
 * the path completion screen.
 *
 * @returns {{
 *   isPathCompleted: boolean,
 *   completedPath: object | null,
 * }}
 */
export function usePathCompletion() {
  const { pathCompletionView: isEnabled } = useFlags();

  const [isPathCompleted, setIsPathCompleted] = useState(false);
  const [completedPath, setCompletedPath] = useState(null);

  const cableRef = useRef(null);

  useEffect(() => {
    if (!isEnabled) return;

    cableRef.current = initPathCompletedChannel(async data => {
      if (data?.status !== "success") return;

      try {
        const { passedBelts } = await fetchUserPathData();
        const roleEnrollmentId = data.roleEnrollmentId;
        const matchingPath = passedBelts?.find(path => path.id === roleEnrollmentId);

        setCompletedPath(matchingPath ?? null);
        setIsPathCompleted(true);
      } catch (error) {
        console.error("Error fetching path data after completion:", error);
      }
    });

    return () => {
      if (cableRef.current) {
        cableRef.current.unsubscribe();
        cableRef.current = null;
      }
    };
  }, [isEnabled]);

  return {
    isPathCompleted,
    completedPath,
  };
}
